import React, { useEffect, useRef, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useGetEmployeeMetricStatusQuery } from '../../pages/metricdashboard/metricDashboardSlice';
import { selectCurrentEmpid, selectCurrentRole } from '../../pages/login/authSlice';
import { MANAGERROLE, SUPERVISORROLE } from '../../constants';

import './CustomGreetingFilter.scss';
import { useGetManagerApproverScorecardsQuery } from '../scoreCardFilter/scoreCardFilterSlice';
import { saveSCfilDate, loadSCfilDate } from '../../utils/dateStorage';

interface Props {
    inputmonth: number;
    setInputMonth: (e: number) => void;
    inputyear: number;
    setInputYear: (e: number) => void;
    scorecardVersionValue: (e: number) => void;
    calendarDate: Date;
    setCalendarDate: (d: Date) => void;
    scorecardNameValue?: (e: string) => void;
}

const MAX_FALLBACK_ATTEMPTS = 2;

const CustomGreetingFilter: React.FC<Props> = ({
    inputmonth,
    setInputMonth,
    inputyear,
    setInputYear,
    scorecardVersionValue,
    calendarDate,
    setCalendarDate,
    scorecardNameValue,
}) => {
    const [scorecardVersion, setScorecardVersion] = useState<number>(0);
    const [scorecardDisable, setScorecardDisable] = useState<boolean>(true);
    const loggedRole = useSelector(selectCurrentRole);
    const loggedEmpNum = useSelector(selectCurrentEmpid);
    const [fallbackStep, setFallbackStep] = useState(0); // 0 = current month, 1 = last month, 2 = last-2 month
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [year, setYear] = useState<number>(inputyear);
    const [month, setMonth] = useState<number>(inputmonth);
    const originalMonthYearRef = useRef<{ month: number; year: number } | null>(null);

    const { eid, scid, tldate } = useParams();

    const {
        data: metricStatusData,
        isFetching: metricStatusFetching,
        isUninitialized: metricStatusUninitialized,
    } = useGetEmployeeMetricStatusQuery(
        {
            employeenumber: loggedEmpNum,
            year: inputyear,
            month: inputmonth,
            scorecardRoleId: loggedRole === SUPERVISORROLE ? 2 : 1,
        },
        { skip: !loggedEmpNum || loggedRole === MANAGERROLE }
    );

    const {
        data: scorecardData,
        isFetching: scorecardDataFetching,
        isUninitialized: scorecardDataUninitialized,
    } = useGetManagerApproverScorecardsQuery(
    { 
        employeenumber: loggedEmpNum,
        year,
        month 
    },
    { skip: loggedRole !== MANAGERROLE }
);
    const isQuerySettled =
        loggedRole === MANAGERROLE
            ? !scorecardDataUninitialized && !scorecardDataFetching
            : !metricStatusUninitialized && !metricStatusFetching;

    const getPreviousMonthYear = (month: number, year: number) => {
        if (month === 1) {
            return { month: 12, year: year - 1 };
        }
        return { month: month - 1, year };
    };

    const applyMonthYear = (newYear: number, newMonth: number) => {
        const newDate = new Date(newYear, newMonth - 1, 1);
        setCalendarDate(newDate);
        setInputMonth(newMonth);
        setInputYear(newYear);
        setMonth(newMonth);
        setYear(newYear);
        saveSCfilDate(newDate);
    };
    useEffect(() => {
    if (scorecardData?.data?.length > 0 && scorecardVersion === 0) {
        const defaultVersion = scorecardData.data[0].scorecard_version_id;
        const defaultMonth = scorecardData.data[0].month;   // 1-12
        const defaultYear = scorecardData.data[0].year;     // e.g. 2025
        if (!defaultMonth || !defaultYear || defaultMonth < 1 || defaultMonth > 12 || defaultYear < 2000) return;
        setInputMonth(defaultMonth);
        setInputYear(defaultYear);
        setCalendarDate(new Date(defaultYear, defaultMonth - 1, 1));
        setScorecardVersion(defaultVersion);
        scorecardVersionValue(defaultVersion);
    }
}, [scorecardData]);
    useEffect(() => {
        if (eid) localStorage.setItem('agentNumber', eid);
        if (scid) localStorage.setItem('scorecardVersion', scid);

        if (tldate) {
            const parsed = new Date(tldate);
            if (!isNaN(parsed.getTime())) {
                setCalendarDate(parsed);
                setInputMonth(parsed.getMonth() + 1);
                setInputYear(parsed.getFullYear());
                saveSCfilDate(parsed);
            }
        } else {
            const storedDate = loadSCfilDate();
            if (storedDate) {
                setCalendarDate(storedDate);
                setInputMonth(storedDate.getMonth() + 1);
                setInputYear(storedDate.getFullYear());
            } else {
                const today = new Date();
                originalMonthYearRef.current = { month: today.getMonth() + 1, year: today.getFullYear() };
                setCalendarDate(today);
                setInputMonth(today.getMonth() + 1);
                setInputYear(today.getFullYear());
            }
        }
        setIsInitialLoad(false);
    }, [eid, scid, tldate]);

    useEffect(() => {
        if (isInitialLoad) return;

        if (!isQuerySettled) return;

        const isFallbackInProgress = originalMonthYearRef.current !== null;

        const hasData =
            loggedRole === MANAGERROLE ? scorecardData?.data?.length > 0 : metricStatusData?.data?.length > 0;

        if (hasData) {
            setScorecardDisable(false);
            const storedVersion = localStorage.getItem('scorecardVersion');
            const defaultVersion =
                loggedRole === MANAGERROLE
                    ? scorecardData.data[0].scorecard_version_id
                    : metricStatusData.data[0].scorecard_version_id;
            const version = storedVersion ? parseInt(storedVersion, 10) : defaultVersion;
            setScorecardVersion(version);
            scorecardVersionValue(version);
            scorecardNameValue?.(
                loggedRole === MANAGERROLE ? scorecardData.data[0].scorecard_name : metricStatusData?.data[0]?.scorecard_name
            );
            originalMonthYearRef.current = null;
            return;
        }

        if (isFallbackInProgress && fallbackStep < MAX_FALLBACK_ATTEMPTS) {
            const { month: prevMonth, year: prevYear } = getPreviousMonthYear(inputmonth, inputyear);
            applyMonthYear(prevYear, prevMonth);
            setFallbackStep((step) => step + 1);
            localStorage.removeItem('scorecardVersion');
            scorecardVersionValue(0);
            return;
        }

        if (isFallbackInProgress) {
            const original = originalMonthYearRef.current as { month: number; year: number };
            applyMonthYear(original.year, original.month);
            originalMonthYearRef.current = null;
        }

        setScorecardDisable(true);
        setScorecardVersion(0);
        scorecardVersionValue(0);
        scorecardNameValue?.('');
    }, [metricStatusData, scorecardData, isQuerySettled, isInitialLoad]);

    const handleDateChange = (e: any) => {
        const selectedDate = e.value;

        setCalendarDate(selectedDate);
        setInputMonth(selectedDate.getMonth() + 1);
        setInputYear(selectedDate.getFullYear());
        setMonth(selectedDate.getMonth() + 1);
        setYear(selectedDate.getFullYear());

        saveSCfilDate(selectedDate);

        localStorage.removeItem('scorecardVersion');
        scorecardVersionValue(0);
        scorecardNameValue?.('');

        originalMonthYearRef.current = null;
        setFallbackStep(0);
        setIsInitialLoad(false);

        if (loggedRole === SUPERVISORROLE) {
            localStorage.removeItem('agentNumber');
        }
    };

    return (
        <div className="custom-filter-container">
            <div className="filter-box">
                <label className="filter-label" htmlFor="calendar-input">
                    Month
                </label>
                <Calendar
                    id="calendar-input"
                    value={calendarDate}
                    onChange={handleDateChange}
                    view="month"
                    dateFormat="MM yy"
                    maxDate={new Date()}
                    showIcon
                    className="custom-calendar"
                />
            </div>
            <div className="filter-box">
                <label className="filter-label" htmlFor="scorecard-dropdown">
                    Scorecard
                </label>
                <Dropdown
                    id="scorecard-dropdown"
                    disabled={scorecardDisable}
                    value={scorecardVersion}
                    onChange={(e) => {
                        setScorecardVersion(e.value);
                        scorecardVersionValue(e.value);
                        localStorage.setItem('scorecardVersion', e.value.toString());
                    }}
                    options={loggedRole === MANAGERROLE ? scorecardData?.data : metricStatusData?.data }
                    optionValue="scorecard_version_id"
                    optionLabel="scorecard_name"
                    placeholder="No Scorecards"
                    className="custom-dropdown"
                />
            </div>
        </div>
    );
};

export default CustomGreetingFilter;
